import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import * as moment from 'moment';
import { TableVirtualScrollDataSource } from 'ng-table-virtual-scroll';

import { BehaviorSubject, Subscription } from 'rxjs';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { Config } from 'src/app/shared/components/errors-placeholder-cell/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { MassMessaging, MassMessagingStatusEnum } from '../../shared';
import { MassMessagingService } from '../mass-messaging.service';

@Component({
  selector: 'app-campaigns-completed',
  templateUrl: './campaigns-completed.component.html',
  styleUrls: ['./campaigns-completed.component.scss'],
})
export class CampaignsCompletedComponent implements OnInit, OnDestroy {
  @Input() dataSourceBS: BehaviorSubject<MassMessaging[]> = new BehaviorSubject([]);

  @Input() config: Config;
  @Input() maxHeight: number;

  dataSource = new TableVirtualScrollDataSource([]);
  displayedColumns = [
    'account',
    'name',
    'numberOfMessagesSuccessfullySent',
    'numberOfMessagesNotSent',
    'numberOfErrors',
    'remainingNumberOfMessagesToSend',
    'read',
    'startTime',
    'creatorEmail',
    'updateTime',
    'scheduledTime',
    'status',
  ];

  MassMessagingStatusEnum = MassMessagingStatusEnum;

  sortActive = 'updateTime';
  sortDirection: 'asc' | 'desc' = 'desc';

  @ViewChild(MatTable) table: MatTable<MassMessaging>;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  private subscriptions = new Subscription();

  constructor(
    public massMessagingService: MassMessagingService,
    private alcmeonContextService: AlcmeonContextService,
    private dialog: MatDialog,
    private basicService: BasicService,
    private errorHandlingService: ErrorHandlingService,
    private customRouter: CustomRouter
  ) {}

  ngOnInit() {
    if (this.alcmeonContextService.isAlcmeonUser()) {
      this.displayedColumns.push('actions');
    }
    this.subscriptions.add(this.dataSourceBS.subscribe(values => this.refreshDataSource(values)));
  }

  sortData($event) {
    const { active, direction } = $event;
    this.sortActive = active;
    this.sortDirection = direction;
    const listSorted = this.dataSource.data.sort((a: MassMessaging, b: MassMessaging) => {
      switch (active) {
        case 'account':
          return (a.tmpFields.account.siteName || '').localeCompare(b.tmpFields.account.siteName);
        case 'numberOfMessagesNotSent':
        case 'numberOfErrors':
        case 'remainingNumberOfMessagesToSend':
        case 'read':
          return a[active] - b[active];
        case 'updateTime':
        case 'scheduledTime':
          return moment(a[active], 'YYYY-MM-DD HH:mm:ss').isAfter(moment(b[active], 'YYYY-MM-DD HH:mm:ss')) ? 1 : -1;
        case 'name':
        case 'numberOfMessagesSuccessfullySent':
        case 'creatorEmail':
        case 'status':
          return this.massMessagingService.getStatusLabel(a).localeCompare(this.massMessagingService.getStatusLabel(b));
        default:
          break;
      }
    });
    if (direction === 'desc') {
      listSorted.reverse();
    }
    this.dataSource.sortData(listSorted, this.sort);
  }

  deleteMassMessaging(massMessaging: MassMessaging) {
    const values = this.dataSourceBS.value;
    const index = values.indexOf(massMessaging);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        description: this.basicService.translate('mass_messaging/confirm_mass_messaging_deletion'),
        cancelBtnText: this.basicService.translate('cancel'),
        confirmBtnText: this.basicService.translate('ok'),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.massMessagingService
          .deleteMassMessaging(massMessaging)
          .pipe(this.errorHandlingService.catchAlertAndThrowHttpError())
          .subscribe({
            next: () => {
              values.splice(index, 1);
              this.refreshDataSource(values);
            },
          });
      }
    });
  }

  private refreshDataSource(values: MassMessaging[]) {
    const dataSource = values.filter(
      ({ status, hide }) =>
        !hide && [MassMessagingStatusEnum.COMPLETED_STATUS, MassMessagingStatusEnum.CANCELLED_STATUS].includes(status)
    );
    this.dataSource = new TableVirtualScrollDataSource(dataSource);
    this.dataSource.sort = this.sort;
  }

  openMassMessaging({ id }) {
    this.customRouter.go('auth.alcmeon.fuse.mass-messaging.details', { id });
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
